import type { GetServerSideProps } from 'next'
import { Card, Empty } from 'antd'
import React, { useCallback, ReactElement, useEffect, useMemo, useState } from 'react'
import ActionsLayout from '@/components/MatchLayout'
import { useMatch } from '@/hooks/swr/useMatch'
import { useChannel } from '@/hooks/useWebSocket'
import WechatShare from '@/components/WechatShare'
import TurnSetter from '@/components/TurnSetter'
import { usePlayers } from '@/hooks/swr/usePlayer'
import { scoreToColumn } from '@/utils/utils'
import Button from '@/components/Button'
import { sumBy, sortBy } from 'lodash-es'
import usePenalties from '@/hooks/swr/usePenalties'
import ColumnResizeTable from '@/components/ColumnResizeTable'
import { ColumnsType } from 'antd/es/table'
import useLogin from '@/hooks/swr/useLogin'

type PropType = { match_id: string }

type turnType = number | string

const PlayersScore: Next.PageWithLayout<PropType> = ({ match_id, wsTurn, showWinner = false, showTurnSetter = true }) => {
  const [turn, setTurn] = useState<turnType>('sum')
  const { current_user } = useLogin()
  const { data: penalties } = usePenalties(match_id, !!current_user?.id)
  useEffect(() => {
    if (wsTurn) {
      setTurn(wsTurn)
    }
  }, [wsTurn])

  const { data: players, mutate: mutatePlayers } = usePlayers(match_id, turn)
  const { data: match, mutate: mutateMatch } = useMatch(match_id)

  // 排名依据(顺序)保存后实时刷新:重拉比赛设置(表头列)与排名
  useChannel(`match-${match_id}`, true, (obj) => {
    if (obj.action === 'mutateMatch') mutateMatch()
    if (obj.action === 'mutateMatch' || obj.action === 'mutatePlayers') mutatePlayers()
  })

  const scores = useMemo(() => {
    if (turn === 'sum') {
      return (match.sort_factors ?? []).map((i) => scoreToColumn(i, match?.match_type))
    }
    if (match.match_type === 'duplicated_spin') {
      return ['ranking_victory_score', 'offset_median_score'].map((i) => scoreToColumn(i, match?.match_type))
    }
    if (match.match_type === 'three_players') {
      return ['victory_score', 'score'].map((i) => scoreToColumn(i, match?.match_type))
    }
    return ['victory_score', 'score', 'rank_up_score'].map((i) => scoreToColumn(i, match?.match_type))
  }, [match.match_type, match.sort_factors, turn])

  const wechatShare = useMemo(() => {
    if (match.name) {
      return <WechatShare res={{ title: match.name }} />
    }
    return null
  }, [match.name])

  const tablePlayers = useMemo(() => {
    let rankablePlayers = players.filter((p) => p.rankable)
    if (!match?.id) {
      return rankablePlayers
    }
    if (match.match_type === 'duplicated_spin' && turn !== 'sum') {
      rankablePlayers.forEach((p) => {
        const historyData = p.history_round_data.find((d) => d.turn_index === turn)
        p.ranking_victory_score = historyData?.ranking_victory_score
        p.offset_median_victory_score = historyData?.offset_median_victory_score
        p.offset_median_score = historyData?.offset_median_score
      })
      rankablePlayers = sortBy(rankablePlayers, (t) => -t.ranking_victory_score * 1000000 - t.offset_median_score)
    }
    let lastPlayer
    rankablePlayers = rankablePlayers.map((player, index) => {
      const p: any = { ...player }
      const penalty_score = sumBy((penalties ?? []).filter((this_penalty) => this_penalty.player_id === p.id).map((this_penalty) => this_penalty.score))
      p.penalty_score = penalty_score
      p.rank_index = index + 1
      let flag = true
      if (index <= 0) {
        flag = false
      } else {
        flag = !match.sort_factors.some((factor) => {
          return `${p[factor]}` !== `${rankablePlayers[index - 1][factor]}`
        })
      }
      if (flag) {
        p.rank_index = lastPlayer?.rank_index
      }
      lastPlayer = p
      return p
    })
    return showWinner ? rankablePlayers.slice(0, match.first_prize + match.second_prize + match.third_prize + match.winning_prize) : rankablePlayers
  }, [match.first_prize, match?.id, match.match_type, match.second_prize, match.sort_factors, match.third_prize, match.winning_prize, penalties, players, showWinner, turn])

  const formatScoreTableListContent = useCallback(
    (v, index) => {
      const userNames = [v.name_user1, v.name_user2, v.name_user3, v.name_user4].filter(Boolean)

      return {
        key: v.id,
        rank: (
          <div className="flex items-center">
            <div>第</div>
            <div className="w-10 text-center font-bold text-red-600">{v?.rank_index || index + 1}</div>
            <div>名</div>
            {v.data?.give_up && <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-solid border-red-500 text-red-500">弃</span>}
          </div>
        ),
        team: <div>{v.index}</div>,
        players: (
          <div className="flex items-center gap-2">
            <div className="text-lg font-bold">{v.name}</div>
            {match?.show_gender && v.gender === '女' && <span className="inline-flex h-5 flex-shrink-0 items-center rounded-full bg-pink-100 px-1.5 text-xs font-bold text-pink-600">♀</span>}
            <div className="name-justify">
              {userNames.map((name, idx) => (
                <span key={+idx} className={`${name.length > 3 ? 'text-sm leading-7' : 'text-base'}`}>
                  {name}
                  {idx < userNames.length - 1 && <span>、</span>}
                </span>
              ))}
            </div>
          </div>
        ),
        scores: scores.map((s) => ({
          [s.data]: JSON.stringify(v[s.data]),
        })),
        penalty_score: v.penalty_score,
        guild: <div className="truncate font-medium">{v.guild}</div>,
      }
    },
    [scores]
  )

  const hasGuildData = useMemo(() => tablePlayers.some((player) => player.guild), [tablePlayers])
  const columns = useMemo(() => {
    const baseColumns: ColumnsType<any> = [
      {
        title: '名次',
        dataIndex: 'rank',
        key: 'rank',
        width: 100,
        fixed: 'left',
      },
      {
        title: '队伍',
        dataIndex: 'team',
        key: 'team',
        width: 100,
      },
      {
        title: '选手',
        dataIndex: 'players',
        key: 'players',
        width: 400,
      },
      ...scores.map((s, index) => {
        return {
          title: s.title,
          dataIndex: s.data,
          key: s.data,
          width: 150,
          render: (__, record) => record.scores[index][s.data],
        }
      }),
    ]

    if (match.match_type === 'spin') {
      baseColumns.push({
        title: '补分/罚分',
        dataIndex: 'penalty_score',
        key: 'penalty_score',
        align: 'center',
        width: 150,
      })
    }

    if (hasGuildData) {
      baseColumns.push({
        title: '所属团体',
        dataIndex: 'guild',
        key: 'guild',
        fixed: 'right',
      })
    }

    if (window?.innerWidth > 500) {
      baseColumns.push({
        title: '',
        dataIndex: '',
        key: 'hidden',
        fixed: 'right',
        width: 0,
      })
    }

    return baseColumns
  }, [scores, match.match_type, hasGuildData])

  const tableContent = useMemo(() => {
    return tablePlayers.length === 0 ? (
      <Card>
        <Empty />
      </Card>
    ) : (
      <ColumnResizeTable columns={columns} dataSource={tablePlayers.map((v, index) => formatScoreTableListContent(v, index))} />
    )
  }, [tablePlayers, columns, formatScoreTableListContent])

  return (
    <div className="h-full w-full">
      {wechatShare}
      {showTurnSetter && (
        <div className="sticky top-6 z-40 mb-6 w-full px-2 lg:px-6">
          <div className="flex w-full flex-nowrap items-center justify-between gap-4 overflow-auto rounded-lg bg-stone-800 px-4 py-1 text-2xl font-bold text-white">
            <div className="flex items-center gap-4">
              <div className="whitespace-nowrap">成绩</div>
              <TurnSetter defaultTurn={turn} setTurn={setTurn} matchTurn={match?.turn ?? 3} firstTurnName="总分" />
            </div>
            <div>
              {match.show_team_scores_via_turn && (
                <Button type="link" href={`/match/${match.id}/players_score_via_turns`} className="mr-2 bg-white">
                  自定义多轮算分
                </Button>
              )}
              <Button
                className="ml-auto"
                type="primary"
                onClick={() => {
                  window.open(`/match/${match.id}/score_download`)
                }}
              >
                下载成绩单
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="px-2 py-6 lg:p-6">{tableContent}</div>
    </div>
  )
}

PlayersScore.getLayout = function getLayout(children: ReactElement) {
  return <ActionsLayout>{children}</ActionsLayout>
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  return {
    props: {
      match_id: ctx.query.match_id,
    },
  }
}

export default PlayersScore
